import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';

const desktopMediaQuery = window.matchMedia('(min-width: 750px)');

/**
 * Product media gallery with one shared media structure:
 * carousel on mobile and static grid on desktop.
 *
 * @extends Component
 */
export class MediaGallery extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );
    desktopMediaQuery.addEventListener('change', this.#syncPresentation, { signal });

    this.#syncPresentation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #syncPresentation = () => {
    const staticDesktopGrid = this.presentation === 'grid' && desktopMediaQuery.matches;
    const slideshow = this.slideshow;

    if (slideshow) slideshow.setAttribute('disabled', String(staticDesktopGrid));
    if (staticDesktopGrid) {
      for (const media of this.media) media.setAttribute('aria-hidden', 'false');
    }
  };

  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (newMediaGallery) this.replaceWith(newMediaGallery);
  };

  #handleZoomMediaSelected = async (event) => {
    if (this.presentation === 'grid' && desktopMediaQuery.matches) return;
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialog = this.refs.zoomDialogComponent;
    if (!zoomDialog?.isBuilt) return;
    zoomDialog.loadHighResolutionImage(zoomDialog.media[index]);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return Array.from(
      this.querySelectorAll(
        ':scope > slideshow-component > slideshow-container > slideshow-slides > slideshow-slide.product-media-container'
      )
    );
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
